import React, {
  memo, useCallback, useMemo, useRef,
} from 'react';

import { useSelector } from 'react-redux';
import JoditEditor from 'jodit-react';
import PropTypes from 'prop-types';

import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';

/** @link https://xdsoft.net/jodit/docs/options.html */
const EDITOR_CONFIG = {
  placeholder: '',
  showPlaceholder: false,
  width: '100%',
  height: 250,
  toolbarButtonSize: 'small',
  statusbar: false,
  showStatusbar: false,
  showXPathInStatusbar: false,
  hidePoweredByJodit: true,
  useNativeTooltipuseNativeTooltip: true,
  defaultLineHeight: 1,
  buttons: [
    'fullsize',
    'preview',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'eraser',
    'ul',
    'ol',
    'font',
    'fontsize',
    'paragraph',
    'image',
    'video',
    'table',
    'link',
    'indent',
    'outdent',
    'align',
    'brush',
  ],
  toolbarAdaptive: false,
  removeButtons: [
    'lineHeight',
    'file',
    'spellcheck',
    'speechRecognize',
    'superscript',
    'subscript',
    'insertclassname',
    'about',
    'symbols',
    'ai-commands',
    'ai-assistant',
    'cut',
    'selectall',
    'redo',
    'undo',
    'copy',
    'paste',
    'source',
    'print',
    'hr',
    'break',
  ],
};

const Description = ({
  title, currentValue, additionalConfig, readOnlyTooltip, tooltip, onChange,
}) => {
  const $editor = useRef(null);
  const picsioTheme = useSelector((state) => state.user.settings.picsioTheme);

  const config = useMemo(() => ({
    ...EDITOR_CONFIG,
    ...additionalConfig,
    theme: picsioTheme === 'dark' || !picsioTheme ? 'dark' : 'default',
  }), [additionalConfig, picsioTheme]);

  const saveDescription = useCallback(
    (value) => {
      if (currentValue !== value) {
        if (onChange) {
          onChange?.(value);
        }
      }
    },
    [currentValue, onChange],
  );

  const isReadOnly = Boolean(additionalConfig?.readonly || additionalConfig?.readOnly);

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip || (isReadOnly ? readOnlyTooltip : null)}>
      <div className="d-flex f-d-column">
        <If condition={title}>
          <span className="PicsioInputLabel m-b-4">{title}</span>
        </If>
        <span />
        <JoditEditor
          ref={$editor}
          value={currentValue}
          config={config}
          onBlur={saveDescription}
        />
      </div>
    </ReadOnlyFieldTooltip>
  );
};

Description.propTypes = {
  title: PropTypes.node,
  currentValue: PropTypes.string.isRequired,
  additionalConfig: PropTypes.shape({}),
  readOnlyTooltip: PropTypes.string,
  tooltip: PropTypes.string,
  onChange: PropTypes.func,
};

Description.defaultProps = {
  title: null,
  additionalConfig: {},
  readOnlyTooltip: null,
  tooltip: null,
  onChange: null,
};

export default memo(Description);
